import { useCallback, useMemo } from "react";
import { useUrlSearch } from "@/shared/hooks/useUrlSearch";
import { useCarouselsQuery } from "../api/queries";
import type {
  CarouselItem,
  PaginationState,
  UseCarouselPageReturn,
} from "../types";

const defaultSearchValues: Record<string, never> = {};

export const useCarouselPage = (): UseCarouselPageReturn => {
  const { page, size, setPage, setSize } = useUrlSearch<Record<string, never>>({
    defaultValues: defaultSearchValues,
    defaultSize: 10,
  });

  const queryParams = useMemo<PaginationState>(
    () => ({ page, size }),
    [page, size],
  );

  const {
    data: queryData,
    isLoading,
    error,
    refetch,
  } = useCarouselsQuery(queryParams);

  const data: CarouselItem[] = queryData?.data ?? [];
  const totalItems = queryData?.totalItems ?? 0;

  const updatePg = useCallback(
    (newPg: Partial<PaginationState>) => {
      if (newPg.page !== undefined) setPage(newPg.page);
      if (newPg.size !== undefined) setSize(newPg.size);
    },
    [setPage, setSize],
  );

  // Compat shim: `useDelete` (used in the page) expects a `setData` setter.
  // The list is React-Query backed, so any mutation just triggers a refetch.
  const setData = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    data,
    setData,
    isLoading,
    error: error as Error | null,
    totalItems,
    pg: { page, size },
    updatePg,
    refetch,
  };
};
